import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type { Thread, Label, ComposeState, Folder } from '../lib/types'
import { MOCK_LABELS, MOCK_THREADS, MOCK_USER, USE_MOCK } from '../lib/mock'

let composeIdCounter = 0

interface MailState {
  // Auth
  user: typeof MOCK_USER | null
  isAuthenticated: boolean

  // Navigation
  activeFolder: Folder
  activeThreadId: string | null

  // Data
  threads: Thread[]
  labels: Label[]
  selectedThreadIds: Set<string>

  // Compose windows (multiple concurrent)
  composeWindows: ComposeState[]

  // Search
  searchQuery: string
  isSearching: boolean

  // Sync
  isSyncing: boolean
  lastSyncAt: Date | null

  // Actions — Navigation
  setActiveFolder: (folder: Folder) => void
  setActiveThread: (id: string | null) => void

  // Actions — Selection
  toggleSelectThread: (id: string) => void
  selectAll: () => void
  clearSelection: () => void

  // Actions — Thread mutations (optimistic)
  markRead: (threadId: string, isRead: boolean) => void
  starThread: (threadId: string, isStarred: boolean) => void
  markImportant: (threadId: string, isImportant: boolean) => void
  snoozeThread: (threadId: string, until: Date) => void
  unsnoozeThread: (threadId: string) => void
  trashThread: (threadId: string) => void
  archiveThread: (threadId: string) => void

  // Actions — Compose
  openCompose: (opts?: Partial<ComposeState>) => void
  closeCompose: (id: string) => void
  updateCompose: (id: string, patch: Partial<ComposeState>) => void
  minimizeCompose: (id: string) => void

  // Actions — Search
  setSearchQuery: (q: string) => void

  // Actions — Data
  setThreads: (threads: Thread[]) => void
  setLabels: (labels: Label[]) => void
  setUser: (user: typeof MOCK_USER | null) => void
}

export const useMailStore = create<MailState>()(
  subscribeWithSelector((set, get) => ({
    user: USE_MOCK ? MOCK_USER : null,
    isAuthenticated: USE_MOCK,
    activeFolder: 'inbox',
    activeThreadId: null,
    threads: USE_MOCK ? MOCK_THREADS : [],
    labels: USE_MOCK ? MOCK_LABELS : [],
    selectedThreadIds: new Set(),
    composeWindows: [],
    searchQuery: '',
    isSearching: false,
    isSyncing: false,
    lastSyncAt: null,

    setActiveFolder: (folder) =>
      set({ activeFolder: folder, activeThreadId: null, selectedThreadIds: new Set() }),

    setActiveThread: (id) => set({ activeThreadId: id }),

    toggleSelectThread: (id) =>
      set((state) => {
        const next = new Set(state.selectedThreadIds)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return { selectedThreadIds: next }
      }),

    selectAll: () =>
      set((state) => ({
        selectedThreadIds: new Set(state.threads.map((t) => t.id)),
      })),

    clearSelection: () => set({ selectedThreadIds: new Set() }),

    markRead: (threadId, isRead) =>
      set((state) => ({
        threads: state.threads.map((t) =>
          t.id === threadId
            ? { ...t, unread_count: isRead ? 0 : 1 }
            : t
        ),
      })),

    starThread: (threadId, isStarred) =>
      set((state) => ({
        threads: state.threads.map((t) =>
          t.id === threadId ? { ...t, is_starred: isStarred } : t
        ),
      })),

    markImportant: (threadId, isImportant) =>
      set((state) => ({
        threads: state.threads.map((t) =>
          t.id === threadId ? { ...t, is_important: isImportant } : t
        ),
      })),

    snoozeThread: (threadId, until) =>
      set((state) => ({
        threads: state.threads.map((t) =>
          t.id === threadId
            ? {
                ...t,
                snoozed_until: until.toISOString(),
                // Remove from inbox labels optimistically
                labels: t.labels.filter((l) => l.slug !== 'inbox'),
              }
            : t
        ),
        activeThreadId: state.activeThreadId === threadId ? null : state.activeThreadId,
      })),

    unsnoozeThread: (threadId) =>
      set((state) => {
        const inboxLabel = state.labels.find((l) => l.slug === 'inbox')
        return {
          threads: state.threads.map((t) =>
            t.id === threadId
              ? {
                  ...t,
                  snoozed_until: undefined,
                  labels: inboxLabel
                    ? [...t.labels.filter((l) => l.slug !== 'inbox'), inboxLabel]
                    : t.labels,
                }
              : t
          ),
        }
      }),

    trashThread: (threadId) =>
      set((state) => ({
        threads: state.threads.filter((t) => t.id !== threadId),
        activeThreadId: state.activeThreadId === threadId ? null : state.activeThreadId,
      })),

    archiveThread: (threadId) =>
      set((state) => ({
        threads: state.threads.filter((t) => t.id !== threadId),
        activeThreadId: state.activeThreadId === threadId ? null : state.activeThreadId,
      })),

    openCompose: (opts) => {
      const id = String(++composeIdCounter)
      set((state) => ({
        composeWindows: [
          ...state.composeWindows,
          {
            id,
            to: [],
            cc: [],
            bcc: [],
            subject: '',
            body_html: '',
            is_minimized: false,
            is_fullscreen: false,
            ...opts,
          },
        ],
      }))
    },

    closeCompose: (id) =>
      set((state) => ({
        composeWindows: state.composeWindows.filter((w) => w.id !== id),
      })),

    updateCompose: (id, patch) =>
      set((state) => ({
        composeWindows: state.composeWindows.map((w) =>
          w.id === id ? { ...w, ...patch } : w
        ),
      })),

    minimizeCompose: (id) =>
      set((state) => ({
        composeWindows: state.composeWindows.map((w) =>
          w.id === id ? { ...w, is_minimized: !w.is_minimized } : w
        ),
      })),

    setSearchQuery: (q) => set({ searchQuery: q, isSearching: q.length > 0 }),

    setThreads: (threads) => set({ threads }),
    setLabels: (labels) => set({ labels }),
    setUser: (user) => set({ user, isAuthenticated: !!user }),
  }))
)
